import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { businessMonth, businessRangeQuery, formatBusiness, monthBounds } from 'config/time';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import ExportRangeDialog, { ExportRange } from 'reusable/ExportRangeDialog';
import { fetchAllPages } from 'reusable/fetchAllPages';
import { useMonthPage } from 'reusable/useMonthPage';
import { CsvCondition, csvDateTime, deviceTimeZoneExportFields } from 'config/csv';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { useTranslation } from 'react-i18next';
import { pageToRecover } from 'reusable/pageRecovery';


interface DriverLicenseProps {
  id?: string;
  /** A36（2026-11-03 和田様の UAT）。ドライバー詳細の上部の月の切り替えで選んだ月（`YYYY-MM`）。既定は今の月。 */
  month?: string;
}

const DriverLicenseSubmissionsTable: React.FC<DriverLicenseProps> = ({
  id,
  month = businessMonth(),
}) => {
  const { t, i18n } = useTranslation('driver');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'create_time' },
  ];
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useMonthPage(month);
  const [sortValue, setSortValue] = useState('');
  const [driverLicense, setDriverLicense] = useState<any[]>([]);

  const formatDateTime = (dateTimeString: string) => ({
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    time: formatBusiness(dateTimeString, 'HH:mm'),
    formattedDate: formatBusiness(dateTimeString, i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy'),
  });

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const exportFields = [
    // 免許証IC記録の項目。測定値・測定時刻（data / testing_time）はこの記録に無い。
    // EV-002。この記録の user は ID だけ。名前・メールは user_info にある。
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、記録の ID を先頭に出す。
    { header: 'Record ID', field: 'id' },
    { header: 'Driver Name', field: 'user_info.name' },
    { header: 'Driver Email', field: 'user_info.email' },
    { header: 'Location', field: 'location_name' },
    // ADM-05。作成日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
    // EV-116 段階3。送信の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];
  // ADM-11-AC2。出力した条件をファイル名に入れる。A36。範囲（period）はエクスポートの画面で足す。
  const exportConditions: CsvCondition[] = [
    ['driver', id],
    ['sort', sortValue],
  ];

  // 免許証IC記録の期間は作成日時。check_in はこの記録に無く、無視されていた。
  // 並び替えは選んだ値を問い合わせに渡す。以前は選択肢だけで反映されていなかった。
  // range が null なら期間で絞らない（エクスポートの全件）。
  const listUrl = useCallback(
    (range: ExportRange, page: number, pageSize: number) => {
      let apiUrl = '/v1/scan_ic_records/?';
      if (range) apiUrl += businessRangeQuery('create_time', range.start, range.end);
      apiUrl += `order_by=${sortValue}&page=${page}&page_size=${pageSize}&user=${id}`;
      return apiUrl;
    },
    [id, sortValue]
  );
  // A36。エクスポートは選んだ範囲（この月／期間／全件）を全ページ読む。
  const fetchExportRows = useCallback(
    (range: ExportRange) => fetchAllPages((page, pageSize) => listUrl(range, page, pageSize)),
    [listUrl]
  );

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // A36。表は選んだ月の分をページ送り。
      const response = await axiosInstance.get(listUrl(monthBounds(month), currentPage, PAGE_SIZE));
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setFetchError(null);
      setCount(response.data.count);
      setDriverLicense(response.data.results);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setDriverLicense([]);
      setFetchError(error);
    }
  }, [listUrl, month, currentPage, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section */}
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <div className="flex flex-row space-x-2">
          {/* A36。エクスポートは「この月／期間を指定／全件」から選ぶ（以前は表示中の1ページ分）。 */}
          <ExportRangeDialog
            month={month}
            exportFields={exportFields}
            name="driver_license"
            conditions={exportConditions}
            fetchRows={fetchExportRows}
          />
        </div>
      </div>
      {/* Table */}
      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">{t('view.license-submission.submit-date')}</h6>
          <h6 className="flex-1 text-center">{t('view.license-submission.submit-time')}</h6>
          <h6 className="flex-1 text-center">{t('view.license-submission.location')}</h6>
          <h6 className="flex-1 text-center">{t('time-zone.device', { ns: 'common' })}</h6>
        </div>
        <div>
          {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
          {driverLicense.map((license, index) => {
            const { time, formattedDate } = formatDateTime(license.create_time);
            return (
              <div key={index} className="flex flex-row py-3 px-4">
                <p className="flex-1 text-center">{formattedDate}</p>
                <p className="flex-1 text-center">{time}</p>
                <p className="flex-1 text-center">{license.location_name}</p>
                {/* EV-116 段階3。送信の時点の端末の時間帯。 */}
                <DeviceTimeZone className="flex-1 text-center" {...deviceTimeZoneOf(license)} at={license.create_time} />
              </div>
            );
          })}
        </div>
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverLicenseSubmissionsTable;